import React from 'react';
import { formatDateTime } from "../../common/dateTime";
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import IconButton, {iconButtonClasses} from "@mui/joy/IconButton";
import CircularProgress from "@mui/joy/CircularProgress";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import Button from "@mui/joy/Button";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import {NotificationService} from "../../common/notification";
import EditorDialog from "./EditorDialog";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {serverSettingsListApi, serverSettingsDeleteApi, serverSettingsAddApi, serverSettingsUpdateApi} from "./serverSettingsApi";
import {ServerSetting} from "../../models/serverSetting";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { RequestResultValue} from "../../common/api/requestResult";
import {getPages} from "../../common/pages/pages";
import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";

const emptyDate = new Date(1900, 1, 1);
const emptySetting = {
    key: '',
    value: '',
    description: '',
    updatedBy: '',
    created: emptyDate,
    updated: emptyDate
};

export function ServerSettingsTable() {
    const [settings, setSettings] = React.useState<ServerSetting[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(0);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [selectedSetting, setSelectedSetting] = React.useState<ServerSetting>(emptySetting);
    const [loading, setLoading] = React.useState(false);
    const id = React.useRef('');

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [page])

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await serverSettingsListApi(page, 20);
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.total / 20));
                setSettings(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const edit = (setting: ServerSetting) => {
        setSelectedSetting(setting);
        setEditorOpen(true);
    }

    const onSave = async (setting: ServerSetting): Promise<boolean> => {
        setLoading(true)
        try {
            let res: RequestResultValue<ServerSetting>;
            if (setting.created === emptyDate) {
                res = await serverSettingsAddApi(setting.key, setting.value, setting.description);
            } else {
                res = await serverSettingsUpdateApi(setting.key, setting.value, setting.description);
            }

            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return false;
            }
            await loadData()
            return true;
        } finally {
            setLoading(false)
        }
    }

    const remove = (key: string) => {
        id.current = key;
        setConfirmOpen(true)
    }

    const onDelete = async () => {
        setLoading(true)
        try {
            const res = await serverSettingsDeleteApi(id.current);
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            await loadData()
        } finally {
            id.current = '';
            setLoading(false)
        }
    }

    return (
        <React.Fragment>

            <ConfirmDialog
                title="Удалить?"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={onDelete}
            >
                Вы уверены что хотите удалить настройку?
            </ConfirmDialog>

            {editorOpen &&
                <EditorDialog setting={selectedSetting}
                              isNew={selectedSetting.created === emptyDate}
                              onConfirm={onSave}
                              open={editorOpen}
                              setOpen={setEditorOpen}/>
            }

            <Box
                className="add-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: {xs: 'none', sm: 'flex'},
                    flexWrap: 'wrap',
                    gap: 1.5,
                    '& > *': {
                        minWidth: {xs: '120px', md: '160px'},
                    },
                }}
            >
                <IconButton
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    onClick={() => edit(emptySetting)}
                >
                    <AddIcon/>
                </IconButton>
            </Box>
            {loading ?
                <CircularProgress/> :
                <>
                    <Sheet
                        className="OrderTableContainer"
                        variant="outlined"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 100, padding: '12px 6px'}}>Ключ</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Значение</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Описание</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Создано</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Обновлено</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Пользователь</th>
                                <th style={{width: 100, padding: '12px 6px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {settings.map((row) => (
                                <tr key={row.key}>
                                    <td>
                                        <Typography level="body-xs">{row.key}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.value}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.description}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{formatDateTime(row.created)}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{formatDateTime(row.updated)}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.updatedBy}</Typography>
                                    </td>
                                    <td>
                                        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                onClick={() => edit(row)}
                                            >
                                                <EditIcon/>
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                onClick={() => remove(row.key)}
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </Box>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box
                        className="Pagination-laptopUp"
                        sx={{
                            pt: 2,
                            gap: 1,
                            [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                            display: {
                                xs: 'none',
                                md: 'flex',
                            },
                        }}
                    >
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<KeyboardArrowLeftIcon/>}
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            Назад
                        </Button>

                        <Box sx={{flex: 1}}/>
                        {getPages(pageCount).map((p) => (
                            <IconButton
                                key={p}
                                size="sm"
                                variant={Number(p) ? 'outlined' : 'plain'}
                                color={page === Number(p) ? "primary" : "neutral"}
                                onClick={() => setPage(Number(p))}
                            >
                                {p}
                            </IconButton>
                        ))}
                        <Box sx={{flex: 1}}/>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            Дальше
                        </Button>
                    </Box>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => edit(emptySetting)}
                        >
                            <AddIcon/>
                        </IconButton>
                        {settings.map((listItem) => (
                            <List key={listItem.key} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <Stack spacing={1} direction={'row'}>
                                            <div>
                                                <Typography gutterBottom sx={{fontWeight: 600}}>
                                                    {listItem.key}: {listItem.value}:
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Описание: {listItem.description}</Typography>
                                                <Typography level="body-xs">
                                                    Создано: {formatDateTime(listItem.created)}
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Обновлено: {formatDateTime(listItem.updated)}
                                                </Typography>
                                                <Typography level="body-xs">
                                                    Пользователь: {listItem.updatedBy}
                                                </Typography>
                                                <Box
                                                    sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                    <Link level="body-sm" component="button"
                                                            onClick={() => edit(listItem)}>
                                                        Редактировать
                                                    </Link>
                                                    <Link level="body-sm" component="button"
                                                            onClick={() => remove(listItem.key)}>
                                                        Удалить
                                                    </Link>
                                                </Box>
                                            </div>
                                        </Stack>
                                    </ListItemContent>
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                    </Box>
                    <Box
                        className="Pagination-mobile"
                        sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                    >
                        <IconButton
                            aria-label="previous page"
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            <KeyboardArrowLeftIcon/>
                        </IconButton>
                        <Typography level="body-sm" sx={{mx: 'auto'}}>
                            Страница {page} из {pageCount}
                        </Typography>
                        <IconButton
                            aria-label="next page"
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            <KeyboardArrowRightIcon/>
                        </IconButton>
                    </Box>
                </>
            }
        </React.Fragment>
    );
}
